/**
 * Client environment.
 *
 * Vite only exposes variables prefixed with `VITE_` to the browser bundle, and
 * everything named here is safe to be public:
 *
 *  - the Supabase URL is a public endpoint;
 *  - the publishable key identifies the project and authenticates as the `anon`
 *    Postgres role, which in this schema has no privileges on any table or
 *    function (migration 0007) — so on its own it grants nothing.
 *
 * Anything genuinely secret — a Supabase secret key (`sb_secret_…`) or the older
 * service-role key, the Eventbrite token, the form session secret — belongs only
 * in Edge Function / serverless environment variables and must never be given a
 * VITE_ prefix. See docs/SECURITY.md.
 */

interface ClientEnv {
  supabaseUrl: string
  /** `sb_publishable_…`, or a legacy anon JWT until you migrate. */
  supabasePublishableKey: string
  /** Base URL of the form-access Edge Function; derived if not set explicitly. */
  formAccessUrl: string
  appName: string
  isProduction: boolean
}

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing environment variable ${name}. Copy .env.example to .env.local and fill it in — see SETUP.md.`
    )
  }
  return value.trim()
}

const supabaseUrl = required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL)

/**
 * Supabase is replacing the legacy JWT keys with publishable/secret keys, so
 * both names are accepted while projects migrate. The new one wins.
 */
const supabasePublishableKey = required(
  'VITE_SUPABASE_PUBLISHABLE_KEY',
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY
)

// Fail fast and loudly rather than letting a server-side key ship to the browser
// because someone pasted the wrong value or renamed a variable in a hurry.
// `sb_secret_` is the new format; `service_role` appears in the legacy JWT body.
if (
  supabasePublishableKey.startsWith('sb_secret_') ||
  /service_role/i.test(supabasePublishableKey)
) {
  throw new Error(
    'VITE_SUPABASE_PUBLISHABLE_KEY looks like a secret key. Secret and service-role keys bypass every RLS policy and must never reach the browser.'
  )
}

/** Treats an unset variable and one set to whitespace as equally absent. */
function optional(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? fallback : trimmed
}

export const env: ClientEnv = {
  supabaseUrl,
  supabasePublishableKey,
  formAccessUrl: optional(
    import.meta.env.VITE_FORM_ACCESS_URL,
    `${supabaseUrl}/functions/v1/form-access`
  ),
  appName: optional(import.meta.env.VITE_APP_NAME, 'Speed Dating Portal'),
  isProduction: import.meta.env.PROD,
}
